import { useCallback, useEffect, useState } from "react";
import { getTransactionSummaryAction } from "../../Actions/TransactionActions";
import {
  MyWifiIcon,
  MyWifiOffIcon,
  MyReceiptLongIcon,
  MyTaskAltIcon,
  MyDoNotDisturbOnIcon,
} from "../../Assets/svg/MyIcons";
import PaycentralTransactionCards from "../PaycentralTransactions/TransactionCards";
import { listTripolaTransactionDataAction } from "../../Actions/TripolaPlanActions";
import { listKycTransactionDataAction } from "../../Actions/KycPlanManagemantAction";
import "./TransactionCards.css";

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return "$ 0.00";
  }
  return `$ ${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Normalize Tripola/KYC list payloads for plain + encrypted interceptor shapes. */
const unwrapTxnPayload = (res) => {
  if (res?.data && (res.data.dashboardStats || Array.isArray(res.data.data) || res.data.status === 200)) {
    return res.data;
  }
  return res || {};
};

const StatCard = ({ title, count, amount, icon: Icon, accent, loading }) => {
  return (
    <article
      className="txn-stat-card"
      style={{
        "--txn-accent": accent,
        "--txn-soft": `${accent}1a`,
      }}
    >
      {loading ? (
        <div className="txn-stat-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="txn-stat-top">
            <p className="txn-stat-label">{title}</p>
            <span className="txn-stat-icon" aria-hidden>
              <Icon />
            </span>
          </div>
          <p className="txn-stat-count">{Number(count || 0).toLocaleString()}</p>
          <div className="txn-stat-meta">
            <span className="txn-stat-amount">{formatAmount(amount)}</span>
          </div>
        </>
      )}
    </article>
  );
};

const TransactionCards = ({
  handleSnackBar = () => {},
  startDate,
  endDate,
  statusFilter,
  tenantFilter,
  searchValue,
  amountFilter,
  shouldRefresh = 0,
  transactionType,
}) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: { count: 0, amount: 0 },
    success: { count: 0, amount: 0 },
    pending: { count: 0, amount: 0 },
    refund: { count: 0, amount: 0 },
    failed: { count: 0, amount: 0 },
    online: { count: 0, amount: 0 },
    offline: { count: 0, amount: 0 },
  });

  const safe = (val, fallback = 0) => val ?? fallback;

  const mapQbotData = useCallback(
    (data) => ({
      total: {
        count: safe(data?.totalTransactionCount),
        amount: safe(data?.totalTransactionAmount),
      },
      success: {
        count: safe(data?.totalSuccessTransactionCount),
        amount: safe(data?.totalSuccessTransactionAmount),
      },
      pending: {
        count: safe(data?.totalPendingTransactionCount),
        amount: safe(data?.totalPendingTransactionAmount),
      },
      refund: {
        count: safe(data?.totalRefundTransactionCount),
        amount: safe(data?.totalRefundTransactionAmount),
      },
      failed: {
        count: safe(data?.totalFailedTransactionCount),
        amount: safe(data?.totalFailedTransactionAmount),
      },
      online: {
        count: safe(data?.totalOnlinePaymentsCount),
        amount: safe(data?.totalOnlinePaymentsAmount),
      },
      offline: {
        count: safe(data?.totalOfflinePaymentsCount),
        amount: safe(data?.totalOfflinePaymentsAmount),
      },
    }),
    []
  );

  const mapTripolaData = useCallback((data) => {
    return {
      total: {
        count: safe(data?.dashboardStats?.overall?.count),
        amount: safe(data?.dashboardStats?.overall?.amount),
      },
      success: {
        count: safe(data?.dashboardStats?.success?.count),
        amount: safe(data?.dashboardStats?.success?.amount),
      },
      pending: {
        count: safe(data?.dashboardStats?.pending?.count),
        amount: safe(data?.dashboardStats?.pending?.amount),
      },
      refund: {
        count: safe(data?.dashboardStats?.refunded?.count),
        amount: safe(data?.dashboardStats?.refunded?.amount),
      },
      failed: {
        count: safe(data?.dashboardStats?.failed?.count),
        amount: safe(data?.dashboardStats?.failed?.amount),
      },
      online: {
        count: safe(data?.dashboardStats?.online?.count),
        amount: safe(data?.dashboardStats?.online?.amount),
      },
      offline: {
        count: safe(data?.dashboardStats?.offline?.count),
        amount: safe(data?.dashboardStats?.offline?.amount),
      },
    };
  }, []);

  const fetchQbotSummary = useCallback(
    async (filters) => {
      setLoading(true);
      try {
        // Action returns summary fields directly (or nested under .data)
        const res = await getTransactionSummaryAction(filters);
        const summary = res?.totalTransactionCount != null ? res : res?.data || res || {};
        setStats(mapQbotData(summary));
      } catch (error) {
        console.error("Qbot transaction summary error:", error);
        handleSnackBar(
          "error",
          error?.response?.data?.message || "Failed to load transactions."
        );
      } finally {
        setLoading(false);
      }
    },
    [mapQbotData, handleSnackBar]
  );

  const fetchTripolaSummary = useCallback(
    async (filters) => {
      setLoading(true);
      try {
        const res = await listTripolaTransactionDataAction(
          1,
          1,
          filters.searchValue || "all",
          filters.startDate,
          filters.endDate,
          filters.status || "all",
          filters.amount || "all",
          filters.tenantFilter || "all"
        );
        const payload = unwrapTxnPayload(res);
        if (payload?.status === 200 || payload?.dashboardStats) {
          setStats(mapTripolaData(payload));
        }
      } catch (error) {
        console.error("Tripola transaction summary error:", error);
        handleSnackBar(
          "error",
          error?.response?.data?.message || "Failed to load Tripola transactions."
        );
      } finally {
        setLoading(false);
      }
    },
    [mapTripolaData, handleSnackBar]
  );

  const fetchKycSummary = useCallback(
    async (filters) => {
      setLoading(true);
      try {
        const res = await listKycTransactionDataAction(
          1,
          1,
          filters.searchValue || "all",
          filters.startDate,
          filters.endDate,
          filters.status || "all",
          filters.amount || "all",
          filters.tenantFilter || "all"
        );
        const payload = unwrapTxnPayload(res);
        if (payload?.status === 200 || payload?.dashboardStats) {
          setStats(mapTripolaData(payload));
        }
      } catch (error) {
        console.error("KYC transaction summary error:", error);
        handleSnackBar(
          "error",
          error?.response?.data?.message || "Failed to load KYC transactions."
        );
      } finally {
        setLoading(false);
      }
    },
    [mapTripolaData, handleSnackBar]
  );

  const fetchSummary = useCallback(
    async (filters) => {
      if (transactionType === "tripola") {
        await fetchTripolaSummary(filters);
      } else if (transactionType === "kyc") {
        await fetchKycSummary(filters);
      } else if (transactionType === "qbot") {
        await fetchQbotSummary(filters);
      }
    },
    [transactionType, fetchTripolaSummary, fetchKycSummary, fetchQbotSummary]
  );

  useEffect(() => {
    if (transactionType === "paycentral") return;

    const filters = {
      startDate,
      endDate,
      status: statusFilter,
      tenantFilter,
      searchValue,
      amount: amountFilter,
    };

    fetchSummary(filters);
    // Load on mount + whenever parent bumps shouldRefresh or portal type changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh, transactionType]);

  if (transactionType === "paycentral") {
    return (
      <PaycentralTransactionCards
        handleSnackBar={handleSnackBar}
        startDate={startDate}
        endDate={endDate}
        statusFilter={statusFilter}
        tenantDb={tenantFilter}
        searchValue={searchValue}
        amountFilter={amountFilter}
        shouldRefresh={shouldRefresh}
      />
    );
  }

  const cards = [
    {
      title: "Total Transactions",
      count: stats.total.count,
      amount: stats.total.amount,
      icon: MyReceiptLongIcon,
      accent: "#22419e",
    },
    {
      title: "Online Payments",
      count: stats.online.count,
      amount: stats.online.amount,
      icon: MyWifiIcon,
      accent: "#0f766e",
    },
    {
      title: "Offline Payments",
      count: stats.offline.count,
      amount: stats.offline.amount,
      icon: MyWifiOffIcon,
      accent: "#475569",
    },
    {
      title: "Successful",
      count: stats.success.count,
      amount: stats.success.amount,
      icon: MyTaskAltIcon,
      accent: "#059669",
    },
    {
      title: "Failed",
      count: stats.failed.count,
      amount: stats.failed.amount,
      icon: MyDoNotDisturbOnIcon,
      accent: "#dc2626",
    },
  ];

  return (
    <div className="txn-stats-row">
      {cards.map((card) => (
        <StatCard key={card.title} {...card} loading={loading} />
      ))}
    </div>
  );
};

export default TransactionCards;
